import { useMemo } from "react"
import { CorrectionPromptDialog } from "@/components/workspace/correction-prompt-dialog"
import { useTranslation } from "@/hooks/use-translation"
import {
  generateLocationReference,
  LOCATION_PANORAMA_FACING,
  PRIMARY_LOCATION_FACING,
  type LocationStorableFacing,
} from "@/lib/locations-api"

/** English model prompt for “more spacious” panorama correction (not localized). */
export const PANORAMA_MORE_SPACIOUS_CORRECTION_PROMPT =
  "Push walls to a more spacious mid-room distance (~3–5 m; nearest wall ≤~35–40% of equatorial band height). Keep the same location identity, materials, lighting, equirectangular 360×180 wrap and seamless edges. Do not paste a close wall plate."

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
  locationId: number
  variantId: number | null
  versionId: number
  versionImageUrl: string
  facing?: LocationStorableFacing
  onSubmitted?: () => void
}

export function LocationCorrectionDialog({
  open,
  onOpenChange,
  projectId,
  locationId,
  variantId,
  versionId,
  versionImageUrl,
  facing = PRIMARY_LOCATION_FACING,
  onSubmitted,
}: Props) {
  const { t } = useTranslation(["location", "common"])

  const presets = useMemo(() => {
    if (facing !== LOCATION_PANORAMA_FACING) return undefined
    return [
      {
        id: "moreSpacious",
        label: t("correction.presets.moreSpacious"),
        prompt: PANORAMA_MORE_SPACIOUS_CORRECTION_PROMPT,
      },
    ]
  }, [facing, t])

  return (
    <CorrectionPromptDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("correction.title")}
      hint={t("correction.hint")}
      placeholder={t("correction.placeholder")}
      versionImageUrl={versionImageUrl}
      closeAriaLabel={t("close", { ns: "common" })}
      cancelLabel={t("cancel", { ns: "common" })}
      generateLabel={t("correction.generate")}
      submittingLabel={t("correction.submitting")}
      presets={presets}
      onSubmit={async (prompt) => {
        await generateLocationReference(projectId, locationId, variantId, {
          correctionPrompt: prompt,
          sourceVersionId: versionId,
          facing,
        })
        onSubmitted?.()
      }}
    />
  )
}
